import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { translations } from '@/utils/translations';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Search, ArrowRight, Clock, Calendar } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Surgeries = () => {
  const { language } = useLanguage();
  const t = translations[language];
  const [surgeries, setSurgeries] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchCategories();
    fetchSurgeries();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchSurgeries = async () => {
    try {
      setLoading(true);
      const url = selectedCategory === 'all' 
        ? `${API}/surgeries` 
        : `${API}/surgeries?category=${selectedCategory}`;
      const response = await axios.get(url);
      setSurgeries(response.data);
    } catch (error) {
      console.error('Error fetching surgeries:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSurgeries();
  }, [selectedCategory]);

  const filteredSurgeries = surgeries.filter((surgery) => {
    const name = surgery[`name_${language}`].toLowerCase();
    const description = surgery[`description_${language}`].toLowerCase();
    const query = searchQuery.toLowerCase();
    return name.includes(query) || description.includes(query);
  });

  return (
    <main className="py-12 bg-gradient-to-br from-gray-50 to-white min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            {t.allSurgeries}
          </h1>
        </div>

        {/* Filters */}
        <div className="mb-8 space-y-4">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <Input
                type="text"
                placeholder={t.searchPlaceholder}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="search-input"
              />
            </div>

            {/* Category Filter */}
            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger className="w-full md:w-64" data-testid="category-select">
                <SelectValue placeholder={t.allCategories} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t.allCategories}</SelectItem>
                {categories.map((cat) => (
                  <SelectItem key={cat.id} value={cat.id}>
                    {cat[`name_${language}`]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex justify-center py-20">
            <div className="spinner"></div>
          </div>
        )}

        {/* Surgeries Grid */}
        {!loading && filteredSurgeries.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredSurgeries.map((surgery) => (
              <Link
                key={surgery.id}
                to={`/surgeries/${surgery.id}`}
                className="card-hover block"
                data-testid={`surgery-card-${surgery.id}`}
              >
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden h-full">
                  <div className="p-6">
                    <h3 className="text-xl font-semibold text-gray-900 mb-3">
                      {surgery[`name_${language}`]}
                    </h3>
                    <p className="text-gray-600 mb-4 line-clamp-3">
                      {surgery[`description_${language}`]}
                    </p>

                    {/* Duration & Recovery */}
                    <div className="flex items-center gap-4 mb-4 text-sm text-gray-500">
                      <div className="flex items-center gap-1">
                        <Clock className="h-4 w-4" />
                        <span>{surgery.duration_days} {t.days}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Calendar className="h-4 w-4" />
                        <span>{surgery.recovery_days} {t.days}</span>
                      </div>
                    </div>

                    {/* Price */}
                    <div className="flex justify-between items-center pt-4 border-t border-gray-100">
                      <div>
                        <p className="text-sm text-gray-500">{t.from}</p>
                        <p className="text-2xl font-bold text-blue-600">
                          €{surgery.price_eur}
                        </p>
                      </div>
                      <Button variant="ghost" className="text-blue-600">
                        <ArrowRight className="h-5 w-5" />
                      </Button>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}

        {/* No Results */}
        {!loading && filteredSurgeries.length === 0 && (
          <div className="text-center py-20" data-testid="no-results">
            <p className="text-xl text-gray-500">{t.noSurgeriesFound}</p>
          </div>
        )}
      </div>
    </main>
  );
};

export default Surgeries;